/**
 * CryptoPulsePanel — Sprint 18
 * Ambient panel on Portfolio Command. 5-minute auto-refresh. Max ~300px height.
 */

import { useState, useEffect } from 'react';
import { Bitcoin, Zap } from 'lucide-react';
import ModuleContainer from './layout/ModuleContainer.jsx';
import AddAssetButton from './AddAssetButton.jsx';

function timeSince(ts) {
  if (!ts) return '—';
  const secs = Math.round((Date.now() - new Date(ts).getTime()) / 1000);
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
  return `${Math.round(secs / 3600)}h ago`;
}

function fmtPct(x) {
  if (x == null) return '—';
  return `${x >= 0 ? '+' : ''}${x.toFixed(1)}%`;
}

const DCA_STATUS = {
  accumulation_zone: { color: '#10b981', icon: '✅', label: 'accumulation zone' },
  overextended:       { color: '#ef4444', icon: '⚠️', label: 'overextended' },
  wait:               { color: '#6b7280', icon: '—',  label: 'wait' },
  no_target:          { color: '#6b7280', icon: '—',  label: 'no target set' },
};

export default function CryptoPulsePanel() {
  const [data, setData] = useState(null);
  useEffect(() => {
    async function load() {
      try {
        const r = await fetch('/api/crypto/pulse');
        if (r.ok) setData(await r.json());
      } catch {}
    }
    load();
    const iv = setInterval(load, 300000);
    return () => clearInterval(iv);
  }, []);

  if (!data) return null;

  const hasDcaIssues = (data.dca_status || []).some(d => d.status === 'accumulation_zone' || d.status === 'overextended');
  const hasOpportunistic = (data.opportunistic || []).length > 0;

  const opportunisticCount = data.opportunistic_count ?? (data.opportunistic?.length || 0);

  function reload() {
    fetch('/api/crypto/pulse').then(r => r.ok ? r.json() : null).then(setData);
  }

  return (
    <ModuleContainer
      title={
        <div className="flex items-center gap-2 w-full">
          <Bitcoin className="w-4 h-4 text-orange-400" /> Crypto Pulse
          <div className="ml-auto">
            <AddAssetButton asset_type="crypto" default_tier="tracked" on_added={reload} label="+ Add" />
          </div>
        </div>
      }
      subtitle={`Updated ${timeSince(data.updated_at)}`}
    >
      <div className="space-y-3 text-[11px] font-mono">
        {/* Hot 24h */}
        {data.hot_24h?.length > 0 && (
          <div>
            <div className="text-[9px] text-korinth-muted uppercase tracking-wider mb-1">Hot 24h</div>
            <div className="space-y-0.5">
              {data.hot_24h.map(h => (
                <a key={h.symbol} href={`/crypto?symbol=${h.symbol}`} className="flex items-center gap-2 px-2 py-1 bg-korinth-bg rounded hover:border-korinth-accent/30 border border-transparent">
                  <span className="text-white font-bold w-12">{h.symbol}</span>
                  <span className={(h.change_24h || 0) >= 0 ? 'text-korinth-accent' : 'text-korinth-danger'}>
                    {fmtPct(h.change_24h)}
                  </span>
                  <span className="text-korinth-text flex-1 truncate text-[10px]">
                    {h.catalyst ? `"${h.catalyst.slice(0, 80)}"` : ''}
                  </span>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Opportunistic count line (always shown if count > 0) */}
        {opportunisticCount > 0 && (
          <a href="/crypto" className="flex items-center gap-1.5 px-2 py-1 bg-korinth-warning/5 border border-korinth-warning/20 rounded hover:border-korinth-warning/40 text-[10px] font-mono">
            <Zap className="w-3 h-3 text-korinth-warning" />
            <span className="text-korinth-warning font-bold">⚡ {opportunisticCount} opportunistic signal{opportunisticCount > 1 ? 's' : ''}</span>
            <span className="text-korinth-muted">— click to view</span>
          </a>
        )}

        {/* Opportunistic signals */}
        {hasOpportunistic && (
          <div>
            <div className="text-[9px] text-korinth-muted uppercase tracking-wider mb-1">Opportunistic signals</div>
            <div className="space-y-0.5">
              {data.opportunistic.slice(0, 3).map(o => (
                <a key={o.symbol} href={`/crypto?symbol=${o.symbol}`} className="flex items-center gap-2 px-2 py-1 bg-korinth-warning/5 border border-korinth-warning/20 rounded hover:border-korinth-warning/50">
                  <span className="text-white font-bold w-16">{o.symbol}</span>
                  {o.change_24h != null && (
                    <span className={(o.change_24h || 0) >= 0 ? 'text-korinth-accent' : 'text-korinth-danger'}>{fmtPct(o.change_24h)}</span>
                  )}
                  <span className="text-korinth-muted text-[10px] ml-auto">{o.added_by}</span>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* DCA status */}
        {data.dca_status?.length > 0 && (
          <div>
            <div className="text-[9px] text-korinth-muted uppercase tracking-wider mb-1">Your DCA</div>
            <div className="space-y-0.5">
              {data.dca_status.filter(d => d.status === 'accumulation_zone' || d.status === 'overextended').map(d => {
                const s = DCA_STATUS[d.status];
                return (
                  <a key={d.symbol} href={`/crypto?symbol=${d.symbol}`} className="flex items-center gap-2 px-2 py-1 bg-korinth-bg rounded hover:border-korinth-accent/30 border border-transparent">
                    <span className="flex-shrink-0">{s.icon}</span>
                    <span className="text-white font-bold w-12">{d.symbol}</span>
                    <span style={{ color: s.color }}>{s.label}</span>
                    {d.delta_pct != null && (
                      <span className="text-korinth-muted text-[10px]">({fmtPct(d.delta_pct)} from ${d.target})</span>
                    )}
                  </a>
                );
              })}
              {!hasDcaIssues && (
                <div className="text-[10px] text-korinth-muted italic px-2">All DCA targets in wait zone</div>
              )}
            </div>
          </div>
        )}

        {/* KAIROS top crypto rec */}
        {data.kairos_top_rec && (
          <div className="flex items-center gap-2 px-2 py-1 bg-korinth-accent/5 border border-korinth-accent/20 rounded">
            <span className="text-[9px] text-korinth-muted uppercase">KAIROS:</span>
            <span className="text-white font-bold">{data.kairos_top_rec.asset}</span>
            <span className={data.kairos_top_rec.direction === 'buy' ? 'text-korinth-accent' : 'text-korinth-danger'}>
              {data.kairos_top_rec.direction?.toUpperCase()}
            </span>
            <span className="text-korinth-accent">{data.kairos_top_rec.conviction_overall?.toFixed(1)}/10</span>
          </div>
        )}

        <a href="/crypto" className="block text-[10px] font-mono text-korinth-accent hover:underline text-center pt-1">
          View full crypto dashboard →
        </a>
      </div>
    </ModuleContainer>
  );
}
